import * as Sentry from '@sentry/browser'

import { isFetchResultSuccessful } from '@/entities/FetchResult'
import {
    NotificationCenterAction,
    NotificationCenterActionCategory,
    NotificationCenterCategory,
} from '@/entities/NotificationCenter/NotificationCenter'
import {
    endEventLoggerApiCall,
    startEventLoggerApiCall,
} from '@/entities/NotificationCenter/NotificationCenterService'
import { CheckInRecord, CheckOutRecord } from '@/entities/TrackRecord/TrackRecord'
import { putTrackRecordsApiCall } from '@/entities/TrackRecord/TrackRecordService'
import { reportMessage } from '@/helpers/errorReporting'
import { TypedDispatch } from '@/store'

import { setError, setTrackRecords } from './trackRecordsReducer'

export const sendRecords = async (
    records: (CheckInRecord | CheckOutRecord)[],
    dispatch: TypedDispatch,
): Promise<boolean> => {
    if (process.env.REACT_APP_APP_ENV !== 'production') {
        console.log('%c Sending records!', 'background: #222; color: #bada55; font-size: 1.5rem;')
        console.log('The following records are sent to API:')
        console.table(records)
    }

    // A breadcrumb (not an event) gives context to errors reported later, without sending personal data to Sentry
    Sentry.addBreadcrumb({
        category: 'track-records',
        message: 'Sending track records to the API',
        data: { count: records.length },
    })

    const responseTracker = await startEventLoggerApiCall(
        NotificationCenterCategory.TRACKS,
        NotificationCenterAction.UPDATED,
        [NotificationCenterActionCategory.BATCH_INSERT],
    )

    if (! isFetchResultSuccessful(responseTracker)) {
        dispatch(setError(responseTracker))
        console.error('[sendRecords]', responseTracker)

        reportMessage('sendRecords', `sendRecords failed with status code ${responseTracker.status}`)

        return false
    }

    const response = await putTrackRecordsApiCall(records, responseTracker.data)

    if (! isFetchResultSuccessful(response)) {
        console.error('[sendRecords]', response)

        reportMessage('sendRecords', `sendRecords failed with status code ${response.status}`)

        return false
    }

    await endEventLoggerApiCall(responseTracker.data)

    return response.data
}

export const sendRecordsAndClearStore = async (
    records: (CheckInRecord | CheckOutRecord)[],
    dispatch: TypedDispatch,
): Promise<boolean> => {
    const result = await sendRecords(records, dispatch)

    if (result) {
        dispatch(setTrackRecords([]))
    }

    return result
}
